import { createSelector } from "@reduxjs/toolkit";
import { getVariationDraft } from "@src/store/slices/experienceBuilder/common/selectors/getVariationDraft";
import { extractPathname } from "@src/store/slices/experienceBuilder/common/utils/extractPathname";
import { experienceBuilderSlice } from "@src/store/slices/experienceBuilder/slice";

/**
 * CSS selector of the element the merchant has selected, for `context.selection`
 * — or `undefined` when there is nothing worth telling the AI.
 *
 * The open draft IS the selection: clicking an element in the preview opens a
 * draft mod for it (`basicMod/thunks/selectElement`), and `getVariationDraft`
 * already scopes drafts to the previewed variation, so a draft belonging to
 * another group can't leak in here.
 *
 * Two things are filtered out:
 * - Drafts with no `selector` (injection mods) — nothing to point at.
 * - Drafts opened on another page. `createdAtUrl` is where the element was
 *   clicked; once the preview has navigated elsewhere the selector usually
 *   doesn't resolve, and handing the model a target that isn't on the page is
 *   worse than handing it none. An absent `createdAtUrl` can't be checked, so
 *   it passes — the model verifies before acting either way.
 *
 * Just the selector, no mod kind or text preview: `visual_builder_locate` reads
 * live tag/text info when the model confirms the target, and the draft's own mod
 * type would clash with the AI-visible types in `context.mods` (a `layout` mod
 * is presented to the AI as `text` — see `layoutModHandler.toAiFormat`).
 *
 * Callers decide WHEN to send it: only on the merchant's own turn, never on
 * continuations (see `VisualBuilderAIVariables.context.selection`).
 */
export const getTurnSelection = createSelector(
  getVariationDraft,
  experienceBuilderSlice.selectors.getLastPath,
  (draft, lastPath): string | undefined => {
    if (!draft || !("selector" in draft) || !draft.selector) return undefined;

    if (
      draft.createdAtUrl &&
      lastPath &&
      extractPathname(draft.createdAtUrl) !== lastPath
    ) {
      return undefined;
    }

    return draft.selector;
  }
);
